import { useCallback, useEffect, useState } from 'react'

import { nextDelay } from '@/app/lib/quant/backoff'
import { quantApi } from '@/app/lib/quant/quantApi'
import { isAlreadyGone, quantErrorMessage } from '@/app/lib/quant/quantErrors'
import type { RunDetail } from '@/app/lib/quant/types'

export type QuantRunState =
  | { status: 'loading' }
  /** `error` is set while the latest poll failed; the last good run stays visible. */
  | { status: 'ready'; run: RunDetail; error?: string }
  | { status: 'not_found' }
  | { status: 'error'; message: string }

const POLL_MS = 1000

/**
 * One run, polled every second while it is `running` so the badge, duration,
 * exit code and summary update in place (spec 036 D-4). `useHealth`-shaped:
 * a cancelled flag guards every state write. Key the caller by run id.
 */
export function useQuantRun(id: string): QuantRunState & { reload: () => void } {
  const [state, setState] = useState<QuantRunState>({ status: 'loading' })
  const [nonce, setNonce] = useState(0)

  useEffect(() => {
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | null = null
    let failures = 0
    function poll() {
      quantApi
        .getRun(id)
        .then((run) => {
          if (cancelled) return
          failures = 0
          setState({ status: 'ready', run })
          if (run.status === 'running') timer = setTimeout(poll, POLL_MS)
        })
        .catch((error: unknown) => {
          if (cancelled) return
          if (isAlreadyGone(error)) {
            setState({ status: 'not_found' })
            return
          }
          const message = quantErrorMessage(error)
          failures += 1
          setState((prev) => (prev.status === 'ready' ? { ...prev, error: message } : { status: 'error', message }))
          timer = setTimeout(poll, nextDelay(failures))
        })
    }
    poll()
    return () => {
      cancelled = true
      if (timer !== null) clearTimeout(timer)
    }
  }, [id, nonce])

  const reload = useCallback(() => setNonce((n) => n + 1), [])
  return { ...state, reload }
}
